/**
 * View-model helpers for dynamic package flight picker (leg-by-leg builder).
 * Data is still sourced from pre-paired deals / linked slices; this layer only
 * projects unique outbound/inbound legs and resolves a valid pair on save.
 */

export function legKey(leg) {
  if (!leg) return "";
  return [
    leg.depDate ?? "",
    leg.arrDate ?? "",
    String(leg.flightNumber ?? ""),
    String(leg.airlineId ?? leg.airlineCode ?? ""),
  ].join("|");
}

/** HH:MM from depTime/arrTime or datetime string (ignores seconds / fare noise). */
function normalizedLegTimeToken(leg, side) {
  if (!leg) return "";
  if (side === "dep") {
    const t = leg.depTime != null ? String(leg.depTime).trim() : "";
    if (t) {
      const m = t.match(/^(\d{1,2}:\d{2})/);
      return m ? m[1] : t.replace(/\s+/g, "");
    }
    const full = String(leg.depDate || "").trim();
    const parts = full.split(/\s+/);
    const tail = parts[1] || "";
    const m2 = tail.match(/^(\d{1,2}:\d{2})/);
    return m2 ? m2[1] : tail.replace(/\s+/g, "");
  }
  const t = leg.arrTime != null ? String(leg.arrTime).trim() : "";
  if (t) {
    const m = t.match(/^(\d{1,2}:\d{2})/);
    return m ? m[1] : t.replace(/\s+/g, "");
  }
  const full = String(leg.arrDate || "").trim();
  const parts = full.split(/\s+/);
  const tail = parts[1] || "";
  const m2 = tail.match(/^(\d{1,2}:\d{2})/);
  return m2 ? m2[1] : tail.replace(/\s+/g, "");
}

/** Dep/arr endpoints (IATA if present) for route identity across fare classes. */
function legRouteToken(leg) {
  if (!leg) return "";
  const dep =
    leg.depAirport?.code != null
      ? String(leg.depAirport.code).trim().toUpperCase()
      : String(leg.depCity ?? "").trim().toUpperCase();
  const arr =
    leg.arrAirport?.code != null
      ? String(leg.arrAirport.code).trim().toUpperCase()
      : String(leg.arrCity ?? "").trim().toUpperCase();
  return `${dep}>${arr}`;
}

/**
 * Same physical itinerary (route + schedule + flight numbers), ignoring fare class / deal id.
 * Used to collapse duplicate package rows that differ only by fare.
 */
export function flightGroupKeyFromPair(departLeg, returnLeg, flightTripType) {
  if (!departLeg) return "";
  const bits = [
    legRouteToken(departLeg),
    normalizedLegTimeToken(departLeg, "dep"),
    normalizedLegTimeToken(departLeg, "arr"),
    String(departLeg.flightNumber ?? "").trim(),
  ];
  if (flightTripType !== "OneWay" && returnLeg) {
    bits.push(
      legRouteToken(returnLeg),
      normalizedLegTimeToken(returnLeg, "dep"),
      normalizedLegTimeToken(returnLeg, "arr"),
      String(returnLeg.flightNumber ?? "").trim()
    );
  }
  return bits.join("|");
}

/**
 * One row per flight group: keep lowest-priced option (same API pricing, fewer UI duplicates).
 */
export function dedupeMergedPairsByCheapestPerFlightGroup(pairs, flightTripType) {
  const map = new Map();
  for (const item of pairs) {
    const gk =
      flightGroupKeyFromPair(item.departLeg, item.returnLeg, flightTripType) || item.pairKey;
    const prev = map.get(gk);
    if (!prev || item.price < prev.price) {
      map.set(gk, { ...item, flightGroupKey: gk, id: gk });
    }
  }
  return [...map.values()];
}

export function legsMatch(a, b) {
  if (!a || !b) return false;
  return (
    a.depDate === b.depDate && String(a.flightNumber ?? "") === String(b.flightNumber ?? "")
  );
}

export function extractLegsFromDeal(deal, flightTripType) {
  let departLeg = null;
  let returnLeg = null;

  if (Array.isArray(deal?.linkedFlights) && deal.linkedFlights.length > 0) {
    const firstLinkedFlight = deal.linkedFlights[0];
    if (firstLinkedFlight?.flight?.[0]?.leg) {
      departLeg = firstLinkedFlight.flight[0].leg;
      returnLeg = firstLinkedFlight.flight[1]?.leg ?? null;
    }
  } else if (deal?.linkedFlights?.flight) {
    const flightsArray = deal.linkedFlights.flight;
    if (Array.isArray(flightsArray) && flightsArray[0]?.leg) {
      departLeg = flightsArray[0].leg;
      returnLeg = flightsArray[1]?.leg ?? null;
    }
  }

  const ok = departLeg && (returnLeg || flightTripType === "OneWay");
  return { departLeg, returnLeg, ok };
}

function dealTotalFare(deal, calculateLDSDealFare, categories) {
  const dealCategory =
    deal?.category ||
    (categories && categories.length > 0 && !categories.includes("ALL")
      ? categories[0]
      : null);
  const price = calculateLDSDealFare({
    ...deal,
    dealCategory,
  });
  return price?.ourTotalFare?.priceWithCommissionAndCreditCardFee ?? 0;
}

/** availableFlights יכול להיות מערך דילים או דיל בודד מהחיפוש */
export function dealsListFromAvailableFlights(availableFlights) {
  if (availableFlights == null) return [];
  return Array.isArray(availableFlights) ? availableFlights : [availableFlights];
}

/**
 * פריטי linkedFlights בתוך דיל — מערך אופציות או אובייקט יחיד.
 */
export function linkedSlicesFromDeal(deal) {
  const lf = deal?.linkedFlights;
  if (lf == null) return [];
  if (Array.isArray(lf)) return lf.filter((x) => x != null);
  return [lf];
}

/**
 * Merge deal-based and linked-based pairs; dedupe by same depart+return legs.
 * Prefers `deal` rows over `linked` when both exist for the same pair.
 */
export function collectMergedPairs({
  linkedFlights,
  availableFlights,
  flightTripType,
  calculatFlightLDSPrice,
  calculateLDSDealFare,
  categories,
}) {
  const raw = [];

  const dealsList = dealsListFromAvailableFlights(availableFlights);
  for (const deal of dealsList) {
    const slices = linkedSlicesFromDeal(deal);
    if (slices.length === 0) {
      const { departLeg, returnLeg, ok } = extractLegsFromDeal(deal, flightTripType);
      if (!ok || !departLeg) continue;
      const price = dealTotalFare(deal, calculateLDSDealFare, categories);
      raw.push({
        pairKey: `${legKey(departLeg)}__${returnLeg ? legKey(returnLeg) : "OW"}`,
        source: "deal",
        deal,
        linkedSlice: null,
        linkedIndex: null,
        departLeg,
        returnLeg: returnLeg || null,
        price,
      });
      continue;
    }

    const multiSlices = slices.length > 1;
    for (let si = 0; si < slices.length; si++) {
      const linkedSlice = slices[si];
      const d0 = linkedSlice?.flight?.[0]?.leg;
      const d1 = linkedSlice?.flight?.[1]?.leg ?? null;
      if (!d0) continue;
      if (flightTripType !== "OneWay" && !d1) continue;
      const price = dealTotalFare(deal, calculateLDSDealFare, categories);
      const dealForRow = { ...deal, linkedFlights: linkedSlice };
      raw.push({
        pairKey: `${legKey(d0)}__${d1 ? legKey(d1) : "OW"}`,
        source: "deal",
        deal: dealForRow,
        linkedSlice,
        linkedIndex: multiSlices ? si : null,
        departLeg: d0,
        returnLeg: d1,
        price,
      });
    }
  }

  if (Array.isArray(linkedFlights)) {
    linkedFlights.forEach((linkedSlice, linkedIndex) => {
      const d0 = linkedSlice?.flight?.[0]?.leg;
      const d1 = linkedSlice?.flight?.[1]?.leg ?? null;
      if (!d0) return;
      if (flightTripType !== "OneWay" && !d1) return;
      const price = calculatFlightLDSPrice(linkedSlice) || 0;
      raw.push({
        pairKey: `${legKey(d0)}__${d1 ? legKey(d1) : "OW"}`,
        source: "linked",
        deal: null,
        linkedSlice,
        linkedIndex,
        departLeg: d0,
        returnLeg: d1,
        price,
      });
    });
  }

  const merged = new Map();
  for (const item of raw) {
    const prev = merged.get(item.pairKey);
    if (!prev) {
      merged.set(item.pairKey, item);
      continue;
    }
    if (item.source === "deal" && prev.source === "linked") {
      merged.set(item.pairKey, item);
      continue;
    }
    if (item.source === "linked" && prev.source === "deal") continue;
    if (item.price < prev.price) merged.set(item.pairKey, item);
  }
  return dedupeMergedPairsByCheapestPerFlightGroup([...merged.values()], flightTripType);
}

export function buildLegOptions(pairs, role, flightTripType) {
  if (role === "return" && flightTripType === "OneWay") return [];
  const map = new Map();
  for (const p of pairs) {
    const leg = role === "depart" ? p.departLeg : p.returnLeg;
    if (!leg) continue;
    const k = legKey(leg);
    const cur = map.get(k);
    const minP = cur ? Math.min(cur.minPrice, p.price) : p.price;
    map.set(k, { leg, minPrice: minP, key: k });
  }
  return [...map.values()];
}

/** Return legs that appear in at least one pair with the chosen outbound (package-legal combos only). */
export function buildInboundOptionsMatchingDepart(pairs, departLeg) {
  if (!departLeg) return [];
  const map = new Map();
  for (const p of pairs) {
    if (!legsMatch(p.departLeg, departLeg)) continue;
    const leg = p.returnLeg;
    if (!leg) continue;
    const k = legKey(leg);
    const cur = map.get(k);
    const minP = cur ? Math.min(cur.minPrice, p.price) : p.price;
    map.set(k, { leg, minPrice: minP, key: k });
  }
  return [...map.values()];
}

export function findPairForSelection(pairs, draftDepart, draftReturn, flightTripType) {
  if (!draftDepart) return null;
  const candidates = pairs.filter((p) => {
    if (!legsMatch(p.departLeg, draftDepart)) return false;
    if (flightTripType === "OneWay") return true;
    if (!draftReturn || !p.returnLeg) return false;
    return legsMatch(p.returnLeg, draftReturn);
  });
  if (!candidates.length) return null;
  candidates.sort((a, b) => a.price - b.price);
  return candidates[0];
}

/** If the matched row is linked-only, try to find an equivalent package deal for pricing. */
export function resolveDealForPair(pair, availableFlights, flightTripType) {
  if (!pair || pair.deal) return pair?.deal ?? null;
  const list = dealsListFromAvailableFlights(availableFlights);
  if (list.length === 0) return null;
  for (const deal of list) {
    const slices = linkedSlicesFromDeal(deal);
    if (slices.length > 0) {
      for (const slice of slices) {
        const d0 = slice?.flight?.[0]?.leg;
        const d1 = slice?.flight?.[1]?.leg ?? null;
        if (!d0) continue;
        if (!legsMatch(d0, pair.departLeg)) continue;
        if (flightTripType === "OneWay") {
          return slices.length > 1 ? { ...deal, linkedFlights: slice } : deal;
        }
        if (d1 && pair.returnLeg && legsMatch(d1, pair.returnLeg)) {
          return slices.length > 1 ? { ...deal, linkedFlights: slice } : deal;
        }
      }
      continue;
    }
    const { departLeg, returnLeg, ok } = extractLegsFromDeal(deal, flightTripType);
    if (!ok || !departLeg) continue;
    if (!legsMatch(departLeg, pair.departLeg)) continue;
    if (flightTripType === "OneWay") return deal;
    if (returnLeg && pair.returnLeg && legsMatch(returnLeg, pair.returnLeg)) return deal;
  }
  return null;
}

export function legDepSortKey(leg) {
  return leg?.depDate || "";
}

export function legDurationTotal(leg) {
  const n = Number(leg?.duration);
  return Number.isFinite(n) ? n : 0;
}

/** True if current legs match one full prepared row (deal or linked slice). */
export function matchesPreparedPackageSelection(
  selectedDepart,
  selectedReturn,
  availableFlights,
  linkedFlights,
  flightTripType
) {
  if (!selectedDepart) return false;
  for (const deal of dealsListFromAvailableFlights(availableFlights)) {
    const slices = linkedSlicesFromDeal(deal);
    const legPairs =
      slices.length > 0
        ? slices.map((slice) => ({
            d0: slice?.flight?.[0]?.leg,
            d1: slice?.flight?.[1]?.leg ?? null,
          }))
        : (() => {
            const { departLeg, returnLeg, ok } = extractLegsFromDeal(
              deal,
              flightTripType
            );
            return ok && departLeg
              ? [{ d0: departLeg, d1: returnLeg || null }]
              : [];
          })();
    for (const { d0, d1 } of legPairs) {
      if (!d0) continue;
      if (flightTripType !== "OneWay" && !d1) continue;
      if (!legsMatch(d0, selectedDepart)) continue;
      if (flightTripType === "OneWay") return true;
      if (selectedReturn && d1 && legsMatch(d1, selectedReturn)) return true;
    }
  }
  if (Array.isArray(linkedFlights)) {
    for (const lf of linkedFlights) {
      const d0 = lf?.flight?.[0]?.leg;
      const d1 = lf?.flight?.[1]?.leg ?? null;
      if (!d0) continue;
      if (!legsMatch(d0, selectedDepart)) continue;
      if (flightTripType === "OneWay") return true;
      if (selectedReturn && d1 && legsMatch(d1, selectedReturn)) return true;
    }
  }
  return false;
}
